import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import FlagIcon from '@mui/icons-material/Flag';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { Box, CircularProgress } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import './App.scss';
import Layout from './components/Layout/Layout';
import { pathsMatch } from './helpers/general';
import socketHandler from './helpers/socket';
import { State } from './models/state';
import Login from './pages/Login/Login';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import ResetPasswordEmail from './pages/ResetPassword/ResetPasswordEmail';
import { PRIVATE_PATHS } from './routes/data';
import PrivateRoute from './routes/PrivateRoute';
import PublicRoute from './routes/PublicRoute';
import { getUser, getUserWithCode } from './store/actions/auth';

const menuItems = [
  {
    name: 'Home',
    icon: <HomeIcon />,
    path: '/',
  },
  {
    name: 'Survey series',
    icon: <QuestionAnswerIcon />,
    path: '/survey-series',
  },
  {
    name: 'Organizations',
    icon: <BusinessIcon />,
    path: '/organizations',
    isAdmin: true,
  },
  {
    name: 'Users',
    icon: <PersonIcon />,
    path: '/users',
    isAdmin: true,
  },
  {
    name: 'Submissions',
    icon: <FormatAlignJustifyIcon />,
    path: '/submissions',
    isAdmin: true,
  },
  {
    name: 'Emails',
    icon: <EmailIcon />,
    path: '/emails',
    isAdmin: true,
  },
  {
    name: 'Countries',
    icon: <FlagIcon />,
    path: '/countries',
    isAdmin: true,
  },
];

function App() {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state: State) => !!state.auth.user.email);
  const { isAdmin } = useSelector((state: State) => state.auth.user);

  const [loading, setLoading] = useState(true);
  const rerenderGuardRef = useRef(false);

  useEffect(() => {
    if (!loading) return;

    const isLoginWithCodePage = pathsMatch('/login/:signInCode', window.location.pathname);
    if (isLoginWithCodePage) {
      if (rerenderGuardRef.current) return;
      rerenderGuardRef.current = true;

      const seachParams = new URLSearchParams(window?.location?.search);
      const fromQueryParam = seachParams.get('from');
      const signInCode = window.location.pathname.split('/')?.[2];

      if (signInCode) {
        localStorage.setItem('isLoggedInWithCode', 'true');
        dispatch(
          getUserWithCode(signInCode, res => {
            if (!res) {
              localStorage.removeItem('isLoggedInWithCode');
            } else {
              window.location.pathname = fromQueryParam || '/';
            }
          }),
        );
      }
      setLoading(false);
    } else {
      dispatch(
        getUser(() => {
          socketHandler();
          setLoading(false);
        }),
      );
    }
  }, [dispatch, loading]);

  const routes = [
    {
      path: '/login',
      element: (
        <PublicRoute isLoggedIn={isLoggedIn}>
          <Login />
        </PublicRoute>
      ),
    },
    {
      path: '/reset-password/:resetCode',
      element: (
        <PublicRoute isLoggedIn={isLoggedIn}>
          <ResetPassword />
        </PublicRoute>
      ),
    },
    {
      path: '/reset-password',
      element: (
        <PublicRoute isLoggedIn={isLoggedIn}>
          <ResetPasswordEmail />
        </PublicRoute>
      ),
    },
    {
      path: '/',
      element: <Layout menuItems={menuItems} />,
      children: PRIVATE_PATHS.map(route => {
        const element = (
          <PrivateRoute isLoggedIn={isLoggedIn} isForAdmin={route.isAdmin} isAdmin={isAdmin}>
            {route.component}
          </PrivateRoute>
        );
        return {
          path: route.path,
          element,
        };
      }),
    },
    {
      path: '/health',
      element: <>Healthy</>,
    },
    {
      path: '*',
      element: <Navigate to='/' />,
    },
  ];
  const router = createBrowserRouter(routes);

  return loading ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        width: '100vw',
      }}
    >
      <CircularProgress />
    </Box>
  ) : (
    <RouterProvider router={router} />
  );
}

export default App;
